/**
 * MKS-144 — full-page ticket view at `/tasks/:taskRef`.
 *
 * Reuses the existing board query (`useBoard`) instead of introducing a
 * dedicated single-task endpoint: the frontend already owns a complete,
 * cached `TaskCardData` row for every task inside
 * `['board', projectId, 'internal']` (the same cache entry the kanban
 * board's side panel reads), and a second serializer would inevitably
 * drift from it. The locate endpoint (`useTaskLocation`, backed by
 * `GET /api/tasks/:taskId/locate`) only resolves a ticket ref (numeric id
 * or code) into the `projectId` it lives on, so this page can join into
 * that same board cache entry — same data, same derivation KanbanBoardPage
 * uses, one source of truth.
 *
 * States, in order: loading (either query pending) → not-found (locate
 * error of any status, board error, or a resolved locate whose task id is
 * not present in the board payload) → loaded. The backend deliberately
 * returns 403 for BOTH "no access" and "task does not exist" (info-
 * disclosure parity, see taskLocateRoutes.ts), so the not-found copy never
 * branches on status code.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTaskLocation } from '../../hooks/useTaskLocation';
import { useBoard } from '../../hooks/useBoard';
import { useProject } from '../../hooks/useProjects';
import { useAuthOptional } from '../../services/authContext';
import { TaskDetailSlideOver } from '../../components/kanban/TaskDetailSlideOver';
import type { TaskCard as TaskCardData } from '../../services/api/board';

export function TaskDetailPage() {
  const { taskRef } = useParams<{ taskRef: string }>();
  const navigate = useNavigate();
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const locationQuery = useTaskLocation(taskRef ?? '');
  const projectId = locationQuery.data?.projectId;
  // useBoard is already `enabled: Boolean(projectId)` — the empty string
  // keeps it idle until the locate query resolves.
  const boardQuery = useBoard(projectId ? String(projectId) : '', 'internal');
  const projectQuery = useProject(projectId);

  const auth = useAuthOptional();
  const currentUser = auth?.user ?? null;
  const isAdmin = auth?.isAdmin ?? false;
  // Same derivation as KanbanBoardPage:167-170 — admin OR the project's PM.
  const canManageMilestones =
    !!currentUser &&
    (isAdmin || projectQuery.data?.projectManager?.id === currentUser.id);

  const task = useMemo<TaskCardData | null>(() => {
    const taskId = locationQuery.data?.taskId;
    if (taskId == null || !boardQuery.data) return null;
    for (const column of boardQuery.data.columns) {
      const found = column.tasks.find((t) => t.id === taskId);
      if (found) return found;
    }
    return null;
  }, [locationQuery.data, boardQuery.data]);

  const isLoading = locationQuery.isLoading || boardQuery.isLoading;
  const isNotFound =
    !isLoading &&
    (locationQuery.isError || boardQuery.isError || (Boolean(locationQuery.data) && !task));

  if (isLoading) {
    return (
      <div className="w-full px-6 py-5">
        <p className="text-sm text-ms-text-muted">Loading ticket…</p>
      </div>
    );
  }

  if (isNotFound || !task || projectId == null) {
    return (
      <div className="w-full px-6 py-5 space-y-2">
        <h1 className="text-lg font-semibold text-ms-text-strong">Ticket not found</h1>
        <p className="text-sm text-ms-text-muted">
          This ticket does not exist, or you do not have access to it.
        </p>
        <Link to="/my-tasks" className="text-sm text-primary-600 hover:underline">
          Back to My Tasks
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-6 pt-4">
        {/* MKS-144 UAT — carry ?openTask back to the board so the ticket
            reopens in the side panel instead of landing on the bare board. */}
        <Link
          to={`/projects/${projectId}/board?openTask=${task.id}`}
          className="text-sm text-primary-600 hover:underline"
        >
          ← Back to {projectQuery.data?.name ?? 'board'}
        </Link>
      </div>
      {updateError && (
        <p className="px-6 pt-2 text-sm text-red-600" role="alert">
          {updateError}
        </p>
      )}
      {deleteError && (
        <p className="px-6 pt-2 text-sm text-red-600" role="alert">
          {deleteError}
        </p>
      )}
      <TaskDetailSlideOver
        variant="page"
        task={task}
        audience="internal"
        isOpen
        onClose={() => navigate(`/projects/${projectId}/board?openTask=${task.id}`)}
        canManageMilestones={canManageMilestones}
        // MKS-364 — same authority as milestone management above (admin OR the
        // project's PM), kept as its own prop so a prop named for milestones
        // never becomes the thing that decides who may close a ticket.
        canManageTimeBookingLock={canManageMilestones}
        projectTaskTypeId={projectQuery.data?.taskTypeId ?? null}
        columns={boardQuery.data?.columns}
        onUpdateError={setUpdateError}
        onDeleteError={setDeleteError}
      />
    </div>
  );
}
